// Typed client for the cloud API's admin surface. The token is passed in per call
// (via `withAuth`) rather than baked into a singleton so tests and the login form can
// swap it without a page reload.

import type {
  ConversationCreditReason,
  MessageFeedbackRating,
  MessageFeedbackReason,
} from "@sage-mcp/shared";

export type ApiErrorCode =
  | "E_UNAUTHORIZED"
  | "E_FORBIDDEN"
  | "E_VALIDATION"
  | "E_NOT_FOUND"
  | "E_CONFLICT"
  | "E_INTERNAL"
  | "E_NETWORK";

export type AdminRole = "superuser" | "reseller";

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number | null;
  readonly details?: unknown;

  constructor(code: ApiErrorCode, status: number | null, message: string, details?: unknown) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

const BASE_URL = (import.meta.env.VITE_CLOUD_API_URL as string | undefined) ?? "http://127.0.0.1:8080";

// The token can be either a JWT (normal user session) or the legacy ADMIN_TOKEN
// (emergency / bootstrap). We forward JWTs as `Authorization: Bearer …`; anything
// short is assumed to be the legacy admin token and sent as X-Admin-Token.
function authHeaders(token: string): Record<string, string> {
  // JWTs are three base64url segments joined by dots. That's a cheap enough test
  // to route without leaking the legacy code path into production auth headers.
  const isJwt = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token);
  return isJwt
    ? { Authorization: `Bearer ${token}` }
    : { "X-Admin-Token": token };
}

async function request<T>(
  token: string,
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers = authHeaders(token);
  // API Gateway rejects requests that declare Content-Type: application/json but
  // ship an empty body, so only set the header when we actually send JSON.
  const hasBody = body !== undefined || method === "POST";
  const serializedBody = hasBody ? JSON.stringify(body ?? {}) : undefined;
  if (serializedBody !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: serializedBody,
    });
  } catch (err) {
    throw new ApiError(
      "E_NETWORK",
      null,
      err instanceof Error ? err.message : String(err),
    );
  }
  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (response.ok) return parsed as T;
  const code =
    ((parsed as { error?: { code?: string } })?.error?.code as ApiErrorCode) ??
    "E_INTERNAL";
  const message =
    (parsed as { error?: { message?: string } })?.error?.message ??
    `HTTP ${response.status}`;
  const details = (parsed as { error?: { details?: unknown } })?.error?.details;
  throw new ApiError(code, response.status, message, details);
}

// -----------------------------------------------------------------------------
// Types mirroring the cloud API response shapes.
// -----------------------------------------------------------------------------

export interface Customer {
  id: string;
  name: string;
  contactEmail: string;
  contactName: string | null;
  company: string | null;
  notes: string | null;
  sageVersion: string;
  // The reseller (admin_users.id) that owns this customer, or null for direct
  // Jalfa customers. resellerEmail/name are pre-joined so the superuser list can
  // display attribution without a second round-trip.
  resellerId: string | null;
  resellerEmail: string | null;
  resellerName: string | null;
  // Customer-level lifecycle gates. Non-null timestamps block every licence
  // attached to the customer at the cloud auth layer. Reasons are free-text
  // notes shown in the dashboard banner and kept in the audit log.
  deactivatedAt: string | null;
  deactivatedReason: string | null;
  pausedAt: string | null;
  pausedReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LicenseTransferInput {
  transferId: string;
  expectedTransferVersion: number;
  replacementPublicKeyFingerprint: string;
}

export interface License {
  id: string;
  customerId: string;
  licenseKey: string;
  label: string | null;
  // "plan" is the license's assigned plan code, retained for older clients.
  plan: string;
  planCode: string;
  planName: string;
  planId: string;
  status: string;
  activationState: "awaiting_activation" | "occupied" | "awaiting_replacement" | "revoking" | "authorization_required";
  transferVersion: number;
  replacementPublicKeyFingerprint: string | null;
  currentMachineId: string | null;
  currentMachineName: string | null;
  trialEndsAt: string | null;
  gracePeriodEndsAt: string | null;
  suspendedAt?: string | null;
  canceledAt?: string | null;
  features: string[];
  writeMode: boolean;
  monthlyBudgetUsd: number;
  createdAt: string;
  updatedAt?: string;
}

// GET /api/admin/usage/platform
export interface PlatformUsageBucketRow {
  purpose: string;
  provider: string;
  modelId: string;
  modelName: string | null;
  calls: number;
  inputTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface PlatformUsageCustomerRow {
  // null when the customer row is gone — the spend still happened.
  customerId: string | null;
  customerName: string | null;
  calls: number;
  costUsd: number;
}

export interface PlatformUsageResponse {
  month: string;
  totals: { costUsd: number; calls: number };
  byPurpose: PlatformUsageBucketRow[];
  byCustomer: PlatformUsageCustomerRow[];
  requests: PlatformUsageRequest[];
  requestPagination: { page: number; pageSize: number; total: number };
}

export interface PlatformUsageRequest extends AdminConversationRequestUsage {
  purpose: string;
  customerId: string | null;
  licenseId: string | null;
  conversationId: string | null;
}

export interface Plan {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  modelId: string;
  // null = titles run on modelId (see cloud-api migration 050).
  titleModelId: string | null;
  // null = question rewriting is OFF for the plan — not a fallback to modelId
  // (see cloud-api migration 059).
  rewriteModelId: string | null;
  guidanceMode: "legacy" | "skills-v1";
  monthlyBudgetUsd: string;
  tolerancePct: string;
  maxToolCallsPerTurn: number;
  retentionDays: number;
  createdAt: string;
  updatedAt: string;
}

export type ReasoningEffort = "none" | "low" | "medium" | "high" | "xhigh" | "max";

export interface LlmModel {
  id: string;
  provider: "openai" | "anthropic";
  providerModelId: string;
  displayName: string;
  contextWindow: number;
  isActive: boolean;
  systemPromptId: string;
  maxOutputTokens: number;
  reasoningEffort: ReasoningEffort;
  createdAt: string;
  updatedAt: string;
}

export interface SystemPrompt {
  id: string;
  label: string;
  content: string;
  isActive: boolean;
  modelCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SystemPromptCostPerModel {
  modelId: string;
  provider: "openai" | "anthropic";
  displayName: string;
  priceInputPerMtok: number | null;
  costPerCall: number | null;
}

export interface SystemPromptCostEstimate {
  tokens: number;
  perModel: SystemPromptCostPerModel[];
}

export interface LlmModelPrice {
  id: string;
  modelId: string;
  effectiveAt: string;
  priceInputPerMtok: string;
  priceCachedPerMtok: string | null;
  priceCacheWritePerMtok: string | null;
  priceOutputPerMtok: string;
  longContext: {
    inputTokenThreshold: number;
    priceInputPerMtok: string;
    priceCachedPerMtok: string | null;
    priceCacheWritePerMtok: string | null;
    priceOutputPerMtok: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface HeartbeatRow {
  machine_id: string;
  received_at: string;
  status: string | null;
  license_status: string | null;
  sage_sql_status: string | null;
  sage_webapi_status: string | null;
  current_config_version: number | null;
  last_error_code: string | null;
  uptime_seconds: number | null;
}

export interface MachineConfigState {
  currentConfigVersion: number | null;
  lastApplyStatus: string | null;
  lastApplyError: string | null;
  lastApplySource: string | null;
  // Companies that failed their connection test on a run that still succeeded. A
  // multi-company apply passes as soon as one company answers, so a green machine can
  // still have unreachable databases — these are the only place that shows up.
  lastApplyWarnings: string[] | null;
  lastAppliedAt: string | null;
}

export interface Machine {
  id: string;
  customerId: string;
  licenseId: string | null;
  machineName: string | null;
  machineFingerprint: string;
  publicKey: string;
  publicKeyFingerprint: string;
  agentVersion: string | null;
  status: "active" | "revoked" | "pending";
  registeredAt: string;
  lastHeartbeatAt: string | null;
  revokedAt: string | null;
  lastHeartbeat: HeartbeatRow | null;
  configState: MachineConfigState | null;
}

// One Sage company (one database) inside a config payload. `id` is the technical slug
// that prefixes the sealed-secret keys, `company` is the Sage company code, and `label`
// is the only one of the three the end user ever sees.
export interface ConfigCompany {
  id: string;
  label?: string;
  sage300Mode?: "development" | "production";
  baseUrl?: string;
  username?: string;
  tenant?: string;
  company?: string;
  apiVersion?: string;
  sqlServer?: string;
  database?: string;
  dbUser?: string;
  dbEncrypt?: boolean;
  dbTrustServerCertificate?: boolean;
  dbMaxRows?: number;
  sageVersion?: string;
  // Soft-delete flag. A disabled row stays in the config (so the app can still
  // resolve its label on conversations that used it) but is filtered out of the
  // picker for new conversations. Toggled by the "Désactiver / Réactiver"
  // buttons on the EditConfig screen.
  disabled?: boolean;
}

export interface ConfigPayload {
  documents?: { storageQuotaBytes?: number };
  mcp?: { port?: number; logLevel?: string };
  sage?: { companies?: ConfigCompany[]; activeCompanyId?: string };
}

export interface ClientConfig {
  id: string;
  // The client_configs.revision counter, bumped on every UPSERT since migration
  // 057 collapsed the versioned draft/publish flow into a single mutable row.
  // Kept named `version` on the wire for compatibility with older admin builds
  // and the agent apply loop that still keys idempotency off this integer.
  version: number;
  // Retained on the type as `false` after 057 — the draft/publish machinery
  // was removed but this field kept its shape so screens that render it don't
  // have to sprout conditional code paths right away.
  isDraft: boolean;
  publishedAt: string | null;
  payload: ConfigPayload;
  createdAt: string;
}

// Reads the companies of a config version, whatever shape the stored payload has.
export function configCompanies(config: ClientConfig | undefined): ConfigCompany[] {
  return config?.payload?.sage?.companies ?? [];
}

// What to show a human for a company: the label if the operator set one, the technical
// slug otherwise.
export function companyDisplayName(company: ConfigCompany): string {
  const label = company.label?.trim();
  return label && label.length > 0 ? label : company.id;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  role: AdminRole;
  disabledAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface LoginResponse {
  token: string;
  expiresIn: number;
  user: AdminUser;
}

async function requestNoAuth<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: { "Content-Type": "application/json" },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new ApiError("E_NETWORK", null, err instanceof Error ? err.message : String(err));
  }
  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (response.ok) return parsed as T;
  const code =
    ((parsed as { error?: { code?: string } })?.error?.code as ApiErrorCode) ??
    "E_INTERNAL";
  const message =
    (parsed as { error?: { message?: string } })?.error?.message ??
    `HTTP ${response.status}`;
  throw new ApiError(code, response.status, message);
}

export const api = {
  // -- Auth ------------------------------------------------------------------
  login: (email: string, password: string) =>
    requestNoAuth<LoginResponse>("POST", "/api/admin/auth/login", { email, password }),
  probe: (token: string) =>
    request<{ ok: true; user: AdminUser }>(token, "GET", "/api/admin/auth/probe"),
  me: (token: string) =>
    request<{ user: AdminUser; emergency?: boolean }>(token, "GET", "/api/admin/auth/me"),
  refresh: (token: string) =>
    request<{ token: string; expiresIn: number }>(token, "POST", "/api/admin/auth/refresh"),

  // -- Admin users -----------------------------------------------------------
  listUsers: (token: string) =>
    request<{ users: AdminUser[]; count: number }>(token, "GET", "/api/admin/users"),
  createUser: (
    token: string,
    body: { email: string; password: string; name?: string; role?: AdminRole },
  ) => request<{ user: AdminUser }>(token, "POST", "/api/admin/users", body),
  updateUserPassword: (token: string, id: string, password: string) =>
    request<{ ok: true }>(token, "PATCH", `/api/admin/users/${id}/password`, { password }),
  setUserDisabled: (token: string, id: string, disabled: boolean) =>
    request<{ user: AdminUser }>(token, "PATCH", `/api/admin/users/${id}/disabled`, { disabled }),

  listCustomers: (token: string) =>
    request<{ customers: Customer[]; count: number }>(token, "GET", "/api/admin/customers"),
  getCustomer: (token: string, id: string) =>
    request<{ customer: Customer }>(token, "GET", `/api/admin/customers/${id}`),
  createCustomer: (token: string, body: {
    name: string;
    contactEmail: string;
    contactName?: string;
    company?: string;
    notes?: string;
  }) => request<{ customer: Customer }>(token, "POST", "/api/admin/customers", body),
  updateCustomer: (token: string, id: string, body: {
    name?: string;
    contactEmail?: string;
    contactName?: string | null;
    company?: string | null;
    notes?: string | null;
    sageVersion?: string;
  }) => request<{ customer: Customer }>(token, "PATCH", `/api/admin/customers/${id}`, body),
  // Reassign a customer to a different reseller. null = make it a direct
  // Jalfa customer. Superuser-only on the server.
  reassignCustomer: (token: string, id: string, resellerId: string | null) =>
    request<{ customer: Customer }>(
      token,
      "PATCH",
      `/api/admin/customers/${id}/reseller`,
      { resellerId },
    ),
  // Customer lifecycle. Deactivate is available to superusers and to the
  // reseller that owns the customer; pause/unpause are superuser-only (the
  // cloud enforces both).
  deactivateCustomer: (token: string, id: string, reason?: string) =>
    request<{ customer: Customer }>(
      token,
      "POST",
      `/api/admin/customers/${id}/deactivate`,
      reason ? { reason } : {},
    ),
  reactivateCustomer: (token: string, id: string) =>
    request<{ customer: Customer }>(
      token,
      "POST",
      `/api/admin/customers/${id}/reactivate`,
      {},
    ),
  pauseCustomer: (token: string, id: string, reason?: string) =>
    request<{ customer: Customer }>(
      token,
      "POST",
      `/api/admin/customers/${id}/pause`,
      reason ? { reason } : {},
    ),
  unpauseCustomer: (token: string, id: string) =>
    request<{ customer: Customer }>(
      token,
      "POST",
      `/api/admin/customers/${id}/unpause`,
      {},
    ),

  // -- Plans -----------------------------------------------------------------
  listPlans: (token: string) =>
    request<{ plans: Plan[]; count: number }>(token, "GET", "/api/admin/plans"),
  createPlan: (token: string, body: {
    code: string;
    name: string;
    modelId: string;
    titleModelId?: string | null;
    rewriteModelId?: string | null;
    guidanceMode?: "legacy" | "skills-v1";
    monthlyBudgetUsd: number;
    tolerancePct?: number;
    maxToolCallsPerTurn?: number;
    retentionDays?: number;
    isActive?: boolean;
  }) => request<{ plan: Plan }>(token, "POST", "/api/admin/plans", body),
  updatePlan: (token: string, id: string, body: {
    name?: string;
    modelId?: string;
    titleModelId?: string | null;
    rewriteModelId?: string | null;
    guidanceMode?: "legacy" | "skills-v1";
    monthlyBudgetUsd?: number;
    tolerancePct?: number;
    maxToolCallsPerTurn?: number;
    retentionDays?: number;
    isActive?: boolean;
  }) => request<{ plan: Plan }>(token, "PATCH", `/api/admin/plans/${id}`, body),
  deactivatePlan: (token: string, id: string) =>
    request<{ plan: Plan }>(token, "POST", `/api/admin/plans/${id}/deactivate`),
  reactivatePlan: (token: string, id: string) =>
    request<{ plan: Plan }>(token, "POST", `/api/admin/plans/${id}/reactivate`),

  // -- LLM models & pricing --------------------------------------------------
  listLlmModels: (token: string) =>
    request<{ models: LlmModel[]; count: number }>(token, "GET", "/api/admin/llm-models"),
  getLlmModel: (token: string, id: string) =>
    request<{ model: LlmModel; prices: LlmModelPrice[] }>(
      token,
      "GET",
      `/api/admin/llm-models/${id}`,
    ),
  createLlmModel: (token: string, body: {
    provider: "openai" | "anthropic";
    providerModelId: string;
    displayName: string;
    contextWindow: number;
    systemPromptId: string;
    maxOutputTokens?: number;
    reasoningEffort?: ReasoningEffort;
    isActive?: boolean;
  }) => request<{ model: LlmModel }>(token, "POST", "/api/admin/llm-models", body),
  updateLlmModel: (token: string, id: string, body: {
    displayName?: string;
    contextWindow?: number;
    systemPromptId?: string;
    maxOutputTokens?: number;
    reasoningEffort?: ReasoningEffort;
    isActive?: boolean;
  }) => request<{ model: LlmModel }>(token, "PATCH", `/api/admin/llm-models/${id}`, body),
  deactivateLlmModel: (token: string, id: string) =>
    request<{ model: LlmModel }>(token, "POST", `/api/admin/llm-models/${id}/deactivate`),
  reactivateLlmModel: (token: string, id: string) =>
    request<{ model: LlmModel }>(token, "POST", `/api/admin/llm-models/${id}/reactivate`),
  createLlmModelPrice: (token: string, modelId: string, body: {
    effectiveAt: string;
    priceInputPerMtok: number;
    priceCachedPerMtok?: number;
    priceCacheWritePerMtok?: number;
    priceOutputPerMtok: number;
    longContext?: {
      inputTokenThreshold: number;
      priceInputPerMtok: number;
      priceCachedPerMtok?: number;
      priceCacheWritePerMtok?: number;
      priceOutputPerMtok: number;
    } | null;
  }) =>
    request<{ price: LlmModelPrice }>(
      token,
      "POST",
      `/api/admin/llm-models/${modelId}/prices`,
      body,
    ),

  // -- System prompts --------------------------------------------------------
  listSystemPrompts: (token: string) =>
    request<{ prompts: SystemPrompt[]; count: number }>(
      token,
      "GET",
      "/api/admin/system-prompts",
    ),
  getSystemPrompt: (token: string, id: string) =>
    request<{ prompt: SystemPrompt }>(token, "GET", `/api/admin/system-prompts/${id}`),
  createSystemPrompt: (token: string, body: { label: string; content: string }) =>
    request<{ prompt: SystemPrompt }>(token, "POST", "/api/admin/system-prompts", body),
  updateSystemPrompt: (
    token: string,
    id: string,
    body: { label?: string; content?: string },
  ) =>
    request<{ prompt: SystemPrompt }>(
      token,
      "PATCH",
      `/api/admin/system-prompts/${id}`,
      body,
    ),
  archiveSystemPrompt: (token: string, id: string) =>
    request<{ prompt: SystemPrompt }>(
      token,
      "POST",
      `/api/admin/system-prompts/${id}/archive`,
    ),
  unarchiveSystemPrompt: (token: string, id: string) =>
    request<{ prompt: SystemPrompt }>(
      token,
      "POST",
      `/api/admin/system-prompts/${id}/unarchive`,
    ),
  estimateSystemPromptCost: (token: string, content: string) =>
    request<SystemPromptCostEstimate>(
      token,
      "POST",
      "/api/admin/system-prompts/estimate-cost",
      { content },
    ),

  listLicenses: (token: string, customerId: string) =>
    request<{ licenses: License[] }>(token, "GET", `/api/admin/customers/${customerId}/licenses`),
  createLicense: (token: string, body: {
    customerId: string;
    planId: string;
    label?: string;
    features?: string[];
    writeMode?: boolean;
  }) => request<{ license: License }>(token, "POST", "/api/admin/licenses", body),
  updateLicense: (
    token: string,
    id: string,
    body: {
      label?: string | null;
        features?: string[];
      writeMode?: boolean;
    },
  ) => request<{ license: License }>(token, "PATCH", `/api/admin/licenses/${id}`, body),
  suspendLicense: (token: string, id: string) =>
    request<{ license: License }>(token, "POST", `/api/admin/licenses/${id}/suspend`),
  transferLicense: (token: string, id: string, body: LicenseTransferInput) =>
    request<{ license: License }>(token, "POST", `/api/admin/licenses/${id}/transfer`, body),
  changeLicensePlan: (token: string, id: string, body: {
    planId: string;
    retentionConfirmation?: {
      currentPlanId: string;
      currentRetentionDays: number;
      proposedRetentionDays: number;
    };
  }) => request<{ license: License }>(token, "POST", `/api/admin/licenses/${id}/plan`, body),
  reactivateLicense: (token: string, id: string) =>
    request<{ license: License }>(token, "POST", `/api/admin/licenses/${id}/reactivate`),
  deleteLicense: (token: string, id: string) =>
    request<null>(token, "DELETE", `/api/admin/licenses/${id}`),

  listMachines: (token: string, customerId: string) =>
    request<{ machines: Machine[] }>(token, "GET", `/api/admin/customers/${customerId}/machines`),
  revokeMachine: (token: string, id: string) =>
    request<{ machine: Machine }>(token, "POST", `/api/admin/machines/${id}/revoke`),
  reactivateMachine: (token: string, id: string) =>
    request<{ machine: Machine }>(token, "POST", `/api/admin/machines/${id}/reactivate`),
  deleteMachine: (token: string, id: string) =>
    request<null>(token, "DELETE", `/api/admin/machines/${id}`),
  getMachinePublicKey: (token: string, id: string) =>
    request<{
      machineId: string;
      customerId: string;
      publicKey: string;
      fingerprint: string;
    }>(token, "GET", `/api/admin/machines/${id}/public-key`),

  // Legacy list endpoint. Since migration 057 a customer holds at most one row,
  // so this returns a list of length 0 or 1. Kept because CustomerDetail.tsx and
  // SealSecrets.tsx still resolve "the current config" through it — swap for
  // getConfig once every consumer moved off.
  listConfigs: (token: string, customerId: string) =>
    request<{ configs: ClientConfig[] }>(
      token,
      "GET",
      `/api/admin/customers/${customerId}/configs`,
    ),
  getConfig: (token: string, customerId: string) =>
    request<{ config: ClientConfig | null }>(
      token,
      "GET",
      `/api/admin/customers/${customerId}/config`,
    ),
  // Replace the customer's live config in place. Bumps the revision counter,
  // records one admin_audit_log entry per company change, and fans out the new
  // payload to every connected machine of the customer over the agent-stream
  // WS. Response echoes the diff so the UI can toast "1 société désactivée,
  // 2 sociétés mises à jour" without recomputing it.
  putConfig: (
    token: string,
    customerId: string,
    payload: Record<string, unknown>,
  ) =>
    request<{
      config: ClientConfig;
      diff: {
        addedIds: string[];
        removedIds: string[];
        disabledIds: string[];
        reactivatedIds: string[];
        updatedIds: string[];
        activeChanged: boolean;
        activeChangedFrom: string | null;
        activeChangedTo: string | null;
        mcpChanged: boolean;
        documentsChanged: boolean;
      };
    }>(
      token,
      "PUT",
      `/api/admin/customers/${customerId}/config`,
      payload,
    ),
  uploadEncryptedSecrets: (
    token: string,
    customerId: string,
    body: {
      machineId: string;
      secrets: Array<{ key: string; ciphertextB64: string }>;
    },
  ) =>
    // The seal targets the customer's single live config; the response echoes
    // its revision counter for informational display.
    request<{ stored: number; configRevision: number }>(
      token,
      "POST",
      `/api/admin/customers/${customerId}/encrypted-secrets`,
      body,
    ),

  // Which secrets a machine already holds. Keys only — the cloud never returns
  // a ciphertext, so this can be shown freely. Drives the pre-checked company
  // boxes on the sealing screen.
  listSealedSecretKeys: (
    token: string,
    customerId: string,
    query: { machineId: string },
  ) =>
    request<{ configRevision: number; keys: Array<{ key: string; createdAt: string }> }>(
      token,
      "GET",
      `/api/admin/customers/${customerId}/encrypted-secrets?machineId=${encodeURIComponent(
        query.machineId,
      )}`,
    ),
  // Revokes a machine's credentials for specific companies. What unchecking a company
  // on the sealing screen calls — without it the checkbox could only ever grant.
  deleteEncryptedSecrets: (
    token: string,
    customerId: string,
    body: { machineId: string; keys: string[] },
  ) =>
    request<{ deleted: number; configRevision: number }>(
      token,
      "DELETE",
      `/api/admin/customers/${customerId}/encrypted-secrets`,
      body,
    ),

  ping: (token: string) => request<{ status: string; version: string }>(token, "GET", "/health"),

  // ---- monitoring (step 11) ------------------------------------------------
  monitoringSummary: (token: string, offlineMinutes?: number) =>
    request<{
      offlineThresholdMinutes: number;
      summary: {
        machinesTotal: number;
        machinesActive: number;
        machinesRevoked: number;
        machinesOnline: number;
        machinesOffline: number;
        applyOkLast24h: number;
        applyFailedLast24h: number;
        errorsLast24h: number;
        runtimeErrorsLast24h: number;
      };
    }>(
      token,
      "GET",
      `/api/admin/monitoring/summary${offlineMinutes ? `?offlineMinutes=${offlineMinutes}` : ""}`,
    ),
  monitoringMachines: (
    token: string,
    opts: { filter?: "all" | "online" | "offline"; offlineMinutes?: number } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.filter) params.set("filter", opts.filter);
    if (opts.offlineMinutes) params.set("offlineMinutes", String(opts.offlineMinutes));
    const qs = params.toString() ? `?${params.toString()}` : "";
    return request<{
      offlineThresholdMinutes: number;
      count: number;
      machines: MonitoringMachine[];
    }>(token, "GET", `/api/admin/monitoring/machines${qs}`);
  },
  listAppVersions: (token: string, channel?: string) => {
    const qs = channel ? `?channel=${encodeURIComponent(channel)}` : "";
    return request<{ appVersions: AppVersion[]; count: number }>(
      token,
      "GET",
      `/api/admin/app-versions${qs}`,
    );
  },
  createAppVersion: (
    token: string,
    body: {
      version: string;
      channel: "stable" | "beta" | "canary";
      downloadUrl: string;
      sha256: string;
      signature?: string;
      required: boolean;
      releaseNotes?: string;
    },
  ) => request<{ appVersion: AppVersion }>(token, "POST", "/api/admin/app-versions", body),
  updateAppVersion: (
    token: string,
    id: string,
    body: {
      downloadUrl?: string;
      sha256?: string;
      signature?: string | null;
      required?: boolean;
      releaseNotes?: string | null;
    },
  ) =>
    request<{ appVersion: AppVersion }>(
      token,
      "PATCH",
      `/api/admin/app-versions/${id}`,
      body,
    ),
  archiveAppVersion: (token: string, id: string) =>
    request<{ appVersion: AppVersion }>(
      token,
      "POST",
      `/api/admin/app-versions/${id}/archive`,
    ),
  unarchiveAppVersion: (token: string, id: string) =>
    request<{ appVersion: AppVersion }>(
      token,
      "POST",
      `/api/admin/app-versions/${id}/unarchive`,
    ),

  // ---- Usage & audit (phase 5) --------------------------------------------
  // Jalfa's own LLM spend for the month — model calls the product makes for
  // itself and bills to nobody. Separate ledger from the customer one; see
  // cloud-api migration 061. Superuser-only server-side.
  platformUsage: (token: string, month?: string, page = 1) => {
    const params = new URLSearchParams({ page: String(page) });
    if (month) params.set("month", month);
    const qs = `?${params}`;
    return request<PlatformUsageResponse>(token, "GET", `/api/admin/usage/platform${qs}`);
  },
  usageSummary: (token: string, month?: string) => {
    const qs = month ? `?month=${encodeURIComponent(month)}` : "";
    return request<UsageSummaryResponse>(token, "GET", `/api/admin/usage/summary${qs}`);
  },
  customerUsage: (token: string, customerId: string, month?: string) => {
    const qs = month ? `?month=${encodeURIComponent(month)}` : "";
    return request<CustomerUsageResponse>(
      token,
      "GET",
      `/api/admin/customers/${customerId}/usage${qs}`,
    );
  },
  auditLog: (
    token: string,
    opts: {
      entity?: string;
      entityId?: string;
      adminUserId?: string;
      since?: string;
      until?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) {
      if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
    }
    const qs = params.toString() ? `?${params.toString()}` : "";
    return request<AuditLogResponse>(token, "GET", `/api/admin/audit-log${qs}`);
  },

  licenseKeyEvents: (
    token: string,
    opts: {
      customerId?: string;
      toLicenseId?: string;
      machineFingerprint?: string;
      outcome?: "approved" | "rejected";
      action?: "swap_check" | "swap_commit";
      since?: string;
      until?: string;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) {
      if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
    }
    const qs = params.toString() ? `?${params.toString()}` : "";
    return request<LicenseKeyEventsResponse>(
      token,
      "GET",
      `/api/admin/license-key-events${qs}`,
    );
  },

  monitoringErrors: (token: string, opts: { limit?: number; sinceMinutes?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.limit) params.set("limit", String(opts.limit));
    if (opts.sinceMinutes) params.set("sinceMinutes", String(opts.sinceMinutes));
    const qs = params.toString() ? `?${params.toString()}` : "";
    return request<{ count: number; errors: MonitoringError[] }>(
      token,
      "GET",
      `/api/admin/monitoring/errors${qs}`,
    );
  },

  listErrors: (token: string, filters: ErrorLogFilters) => {
    const params = new URLSearchParams();
    const set = (k: string, v: unknown) => {
      if (v == null || v === "") return;
      if (Array.isArray(v)) {
        for (const item of v) params.append(k, String(item));
      } else {
        params.set(k, String(v));
      }
    };
    set("origin", filters.origin);
    set("level", filters.level);
    set("machineId", filters.machineId);
    set("customerId", filters.customerId);
    set("sinceMinutes", filters.sinceMinutes);
    set("untilMinutes", filters.untilMinutes);
    set("q", filters.q);
    set("limit", filters.limit ?? 100);
    if (filters.cursor) {
      params.set("cursorLoggedAt", filters.cursor.loggedAt);
      params.set("cursorId", filters.cursor.id);
    }
    const qs = params.toString() ? `?${params.toString()}` : "";
    return request<ErrorLogListResponse>(token, "GET", `/api/admin/errors${qs}`);
  },

  errorFacets: (token: string, sinceMinutes = 60 * 24 * 7) => {
    return request<ErrorLogFacets>(
      token,
      "GET",
      `/api/admin/errors/facets?sinceMinutes=${sinceMinutes}`,
    );
  },

  errorDetail: (token: string, id: string) => {
    return request<{ error: ErrorLogEntry }>(token, "GET", `/api/admin/errors/${id}`);
  },

  listConversations: (token: string, filters: AdminConversationFilters) => {
    const params = new URLSearchParams();
    const set = (k: string, v: unknown) => {
      if (v == null || v === "") return;
      params.set(k, String(v));
    };
    set("customerId", filters.customerId);
    set("machineId", filters.machineId);
    set("sinceMinutes", filters.sinceMinutes);
    set("untilMinutes", filters.untilMinutes);
    set("q", filters.q);
    if (filters.includeArchived) params.set("includeArchived", "true");
    set("feedback", filters.feedback);
    set("feedbackReason", filters.feedbackReason);
    set("failures", filters.failures);
    set("limit", filters.limit ?? 100);
    if (filters.cursor) {
      params.set("cursorUpdatedAt", filters.cursor.updatedAt);
      params.set("cursorId", filters.cursor.id);
    }
    const qs = params.toString() ? `?${params.toString()}` : "";
    return request<AdminConversationListResponse>(
      token,
      "GET",
      `/api/admin/conversations${qs}`,
    );
  },

  conversationFacets: (token: string, sinceMinutes = 60 * 24 * 30) => {
    return request<AdminConversationFacets>(
      token,
      "GET",
      `/api/admin/conversations/facets?sinceMinutes=${sinceMinutes}`,
    );
  },

  conversationDetail: (token: string, id: string) => {
    return request<{ conversation: AdminConversationDetail }>(
      token,
      "GET",
      `/api/admin/conversations/${id}`,
    );
  },

  // Superuser only — the cloud answers 403 to anyone else. Crediting also hides
  // the conversation from the customer's app, which is why the dialog says so.
  creditConversation: (
    token: string,
    id: string,
    body: { reason: ConversationCreditReason; note?: string },
  ) => {
    return request<{ credit: ConversationCredit; creditedCostUsd: number }>(
      token,
      "POST",
      `/api/admin/conversations/${id}/credit`,
      body,
    );
  },

  uncreditConversation: (token: string, id: string) => {
    return request<{ credit: null }>(
      token,
      "DELETE",
      `/api/admin/conversations/${id}/credit`,
    );
  },
};

export type ErrorLogOrigin = "cloud-api" | "local-agent" | "mcp-server";
export type ErrorLogLevel = "error" | "warn";

export interface ErrorLogEntry {
  id: string;
  loggedAt: string;
  receivedAt: string;
  origin: ErrorLogOrigin;
  level: ErrorLogLevel;
  message: string;
  source: string | null;
  errorCode: string | null;
  stack: string | null;
  requestId: string | null;
  customerId: string | null;
  machineId: string | null;
  agentVersion: string | null;
  context: Record<string, unknown> | null;
}

export interface ErrorLogCursor {
  loggedAt: string;
  id: string;
}

export interface ErrorLogFilters {
  origin?: ErrorLogOrigin[];
  level?: ErrorLogLevel[];
  machineId?: string;
  customerId?: string;
  sinceMinutes?: number;
  untilMinutes?: number;
  q?: string;
  limit?: number;
  cursor?: ErrorLogCursor | null;
}

export interface ErrorLogListResponse {
  count: number;
  errors: ErrorLogEntry[];
  nextCursor: ErrorLogCursor | null;
}

export interface ErrorLogFacetCustomer {
  customerId: string;
  customerName: string;
  count: number;
}
export interface ErrorLogFacetMachine {
  machineId: string;
  machineName: string | null;
  customerId: string;
  customerName: string;
  count: number;
}
export interface ErrorLogFacets {
  origins: ErrorLogOrigin[];
  customers: ErrorLogFacetCustomer[];
  machines: ErrorLogFacetMachine[];
}

export interface MonitoringMachine {
  machineId: string;
  customerId: string;
  customerName: string;
  machineName: string | null;
  machineStatus: string;
  agentVersion: string | null;
  online: boolean;
  lastHeartbeatAt: string | null;
  lastHeartbeatAgeSeconds: number | null;
  licenseStatus: string | null;
  hbStatus: string | null;
  sageSqlStatus: string | null;
  sageWebApiStatus: string | null;
  lastErrorCode: string | null;
  uptimeSeconds: number | null;
  currentConfigVersion: number | null;
  lastApplyStatus: string | null;
  lastApplySource: string | null;
  lastApplyError: string | null;
}

export interface AppVersion {
  id: string;
  version: string;
  channel: "stable" | "beta" | "canary";
  downloadUrl: string;
  sha256: string;
  signature: string | null;
  required: boolean;
  releaseNotes: string | null;
  publishedAt: string;
  createdAt: string;
  archivedAt: string | null;
}

// An admin-issued credit on a whole conversation. `null` everywhere for a
// reseller: the cloud only fills these fields for superusers, since a credit is an
// internal Jalfa arbitration (the net cost, on the other hand, is everyone's).
export interface ConversationCredit {
  reason: ConversationCreditReason;
  note: string | null;
  creditedByEmail: string | null;
  createdAt: string;
}

export interface CreditedConversationRow {
  conversationId: string;
  title: string;
  licenseId: string;
  reason: ConversationCreditReason;
  note: string | null;
  creditedByEmail: string | null;
  creditedAt: string;
  costUsd: number;
}

export interface LicenseUsageRow {
  customerId: string;
  licenseId: string;
  licenseKey: string;
  label: string | null;
  plan: {
    id: string; code: string; name: string;
    monthlyBudgetUsd: number; tolerancePct: number; hardCapUsd: number;
  };
  monthlyBudgetUsd: number;
  costUsd: number;
  creditedUsd: number | null;
  heldReservationsUsd: number;
  projectedUsd: number;
  remainingBudgetUsd: number;
  remainingHardCapUsd: number;
  budgetUsedPct: number;
  hardCapUsedPct: number;
  exhausted: boolean;
  inputTokens: number;
  outputTokens: number;
  turnCount: number;
}

export interface UsageSummaryRow {
  customerId: string;
  customerName: string;
  licenses: LicenseUsageRow[];
  // Descriptive sums of independent allowances, never a shared quota.
  allowanceTotals: { monthlyBudgetUsd: number; hardCapUsd: number };
  costUsd: number;
  creditedUsd: number | null;
  heldReservationsUsd: number;
  projectedUsd: number;
  inputTokens: number;
  outputTokens: number;
  turnCount: number;
}

export interface UsageSummaryResponse {
  month: string;
  totals: {
    costUsd: number;
    creditedUsd: number | null;
    heldReservationsUsd: number;
    inputTokens: number;
    outputTokens: number;
  };
  customers: UsageSummaryRow[];
}

export interface CustomerUsageResponse {
  customer: { id: string; name: string };
  allowanceTotals: { monthlyBudgetUsd: number; hardCapUsd: number };
  month: string;
  totals: {
    costUsd: number;
    creditedUsd: number | null;
    heldReservationsUsd: number;
    projectedUsd: number;
    inputTokens: number;
    cachedInputTokens: number;
    outputTokens: number;
    turnCount: number;
  };
  byLicense: LicenseUsageRow[];
  byModel: Array<{
    modelId: string;
    provider: string;
    displayName: string;
    costUsd: number;
    inputTokens: number;
    outputTokens: number;
    turnCount: number;
  }>;
  // creditedUsd is null for a reseller — same arbitration rule as totals.creditedUsd.
  history: Array<{
    month: string;
    costUsd: number;
    creditedUsd: number | null;
    turnCount: number;
  }>;
  creditedConversations: CreditedConversationRow[];
  heldReservations: Array<{
    id: string;
    licenseId: string;
    turnId: string | null;
    estimatedCostUsd: number;
    createdAt: string;
  }>;
}

export interface AuditLogEntry {
  id: string;
  adminUserId: string | null;
  adminEmail: string | null;
  entity: string;
  entityId: string | null;
  action: string;
  diff: Record<string, unknown>;
  createdAt: string;
}

export interface AuditLogResponse {
  total: number;
  count: number;
  entries: AuditLogEntry[];
}

export interface LicenseKeyEventEntry {
  id: string;
  createdAt: string;
  action: "swap_check" | "swap_commit";
  outcome: "approved" | "rejected";
  reason: string | null;
  customerId: string | null;
  customerName: string | null;
  toLicenseId: string | null;
  toLicenseLabel: string | null;
  fromLicenseKeyLast4: string | null;
  toLicenseKeyLast4: string;
  machineFingerprint: string;
  publicKeyFingerprint: string | null;
}

export interface LicenseKeyEventsResponse {
  total: number;
  count: number;
  entries: LicenseKeyEventEntry[];
}

// Discriminated union of the two error sources surfaced by
// /api/admin/monitoring/errors: bounded heartbeat error codes reported by the
// agent, and free-form failed/rollback applies from apply_events.
export type MonitoringError = HeartbeatMonitoringError | ApplyMonitoringError;

export interface HeartbeatMonitoringError {
  kind: "heartbeat";
  id: string;
  receivedAt: string;
  machineId: string;
  machineName: string | null;
  customerId: string;
  customerName: string;
  lastErrorCode: string;
  agentVersion: string | null;
  sageSqlStatus: string | null;
  sageWebApiStatus: string | null;
}

export interface ApplyMonitoringError {
  kind: "apply";
  id: string;
  receivedAt: string;
  machineId: string;
  machineName: string | null;
  customerId: string;
  customerName: string;
  status: string;
  error: string | null;
  configVersion: number | null;
  source: string | null;
}

// ---- Admin conversations ----

export interface TokenBreakdown {
  inputTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  outputTokens: number;
}

// Roll-up of the end user's per-message ratings. `negativeCount > 0` is what
// "flagged" means throughout this console.
export interface AdminConversationFeedbackSummary {
  negativeCount: number;
  positiveCount: number;
  reasons: MessageFeedbackReason[];
  lastNegativeAt: string | null;
}

export interface AdminConversationRow {
  id: string;
  title: string;
  customerId: string;
  customerName: string;
  licenseId: string;
  machineId: string | null;
  machineName: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  tokens: TokenBreakdown;
  costUsd: number;
  turnCount: number;
  messageCount: number;
  feedback: AdminConversationFeedbackSummary;
  failures: AdminConversationFailureSummary;
  credit: ConversationCredit | null;
}

export interface AdminConversationCursor {
  updatedAt: string;
  id: string;
}

// "flagged" = at least one thumbs-down; "unflagged" = none (a conversation with
// only thumbs-up is unflagged); "rated" = the user reacted either way.
export type AdminConversationFeedbackFilter = "flagged" | "unflagged" | "rated";

// What actually went wrong, as opposed to `feedback`, which is what the user
// reported. "any" = at least one failed tool call or failed turn; "tool" /
// "turn" isolate one source; "clean" is the complement of "any".
export type AdminConversationFailureFilter = "any" | "tool" | "turn" | "clean";

export interface AdminConversationFailureSummary {
  toolFailureCount: number;
  failedTurnCount: number;
  // Distinct tool names that failed, and distinct turn error codes — both fed to
  // the row tooltip so an operator sees the shape of the failure without opening
  // the conversation.
  toolNames: string[];
  turnErrorCodes: string[];
  lastFailureAt: string | null;
}

export interface AdminConversationFilters {
  customerId?: string;
  machineId?: string;
  sinceMinutes?: number;
  untilMinutes?: number;
  q?: string;
  includeArchived?: boolean;
  feedback?: AdminConversationFeedbackFilter | null;
  // Narrows to a single reported reason; implies the flagged filter.
  feedbackReason?: MessageFeedbackReason | null;
  failures?: AdminConversationFailureFilter | null;
  limit?: number;
  cursor?: AdminConversationCursor | null;
}

export interface AdminConversationListResponse {
  count: number;
  conversations: AdminConversationRow[];
  nextCursor: AdminConversationCursor | null;
}

export interface AdminConversationFacetCustomer {
  customerId: string;
  customerName: string;
  count: number;
}

export interface AdminConversationFacetMachine {
  machineId: string;
  machineName: string | null;
  customerId: string;
  customerName: string;
  count: number;
}

export interface AdminConversationFacetFeedbackReason {
  reason: MessageFeedbackReason;
  count: number;
}

export interface AdminConversationFacets {
  customers: AdminConversationFacetCustomer[];
  machines: AdminConversationFacetMachine[];
  feedbackReasons: AdminConversationFacetFeedbackReason[];
  flaggedCount: number;
  failureCounts: { any: number; tool: number; turn: number };
}

export type AdminMessageRole = "user" | "assistant" | "system" | "tool";

export type AdminMessageContentPart =
  | { type: "text"; text: string }
  | {
      type: "tool_use";
      providerCallId: string;
      toolName: string;
      arguments: Record<string, unknown>;
    }
  | {
      type: "tool_result";
      providerCallId: string;
      toolName: string;
      content: unknown;
      isError?: boolean;
    };

export interface AdminMessageContent {
  parts: AdminMessageContentPart[];
  truncated?: boolean;
}

export interface AdminConversationMessageUsage {
  tokens: TokenBreakdown;
  costUsd: number;
  modelId: string;
  modelDisplayName: string | null;
  provider: string;
}

// What the end user reported about this specific reply, shown inline in the
// conversation view.
export interface AdminMessageFeedback {
  rating: MessageFeedbackRating;
  reason: MessageFeedbackReason | null;
  comment: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminConversationMessage {
  id: string;
  role: AdminMessageRole;
  content: AdminMessageContent;
  parentTurnId: string | null;
  truncatedByQuota: boolean;
  createdAt: string;
  usage: AdminConversationMessageUsage | null;
  feedback: AdminMessageFeedback | null;
}

export interface AdminConversationRequestUsage {
  id: string;
  turnId: string | null;
  providerRequestId: string | null;
  provider: string;
  modelId: string;
  createdAt: string;
  tokens: TokenBreakdown;
  costUsd: number;
  appliedPricingTier: "standard" | "long_context" | null;
  inputTokenThresholdSnapshot: number | null;
  rates: {
    inputPerMtok: number;
    cachedPerMtok: number | null;
    cacheWritePerMtok: number | null;
    outputPerMtok: number;
  };
}

export interface AdminConversationTurn {
  requests: AdminConversationRequestUsage[];
  guidance: Record<string, unknown>;
  turnId: string;
  status: string;
  provider: string;
  providerModelId: string;
  modelId: string;
  modelDisplayName: string | null;
  startedAt: string;
  endedAt: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  tokens: TokenBreakdown;
  costUsd: number;
}

export interface AdminConversationDetail {
  id: string;
  title: string;
  customerId: string;
  customerName: string;
  licenseId: string;
  machineId: string | null;
  machineName: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  totals: {
    tokens: TokenBreakdown;
    costUsd: number;
    titleCostUsd: number;
    turnCount: number;
    messageCount: number;
  };
  feedback: AdminConversationFeedbackSummary;
  credit: ConversationCredit | null;
  turns: AdminConversationTurn[];
  auxiliaryRequests: AdminConversationRequestUsage[];
  messages: AdminConversationMessage[];
}
